import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { KillEvent, PlayerHurtEvent, BulletImpactEvent, EntityStartEvent, GrenadeDetonateEvent, FlashBlindEvent, ParsedReplay, SmokeBreak } from './replayParser';
import { renderFrame, mapImageMappings, mapConfigs, worldToCanvas, multiLevelMaps, KillFeedEntry, TracerEntry, GrenadeTrajectory, FlashPopEntry, DeathMarker, HitMarkerEntry, filterFrameByLevel, filterWorldByLevel, buildEntityZLookup, filterByPlayerZ, EntityZData } from './replayRenderer';
import { preloadIcons, PreloadedIcons } from './weaponIcons';
import { drawMapControl } from './mapControl/mapControlRenderer';
import type { MapControlData } from './mapControl/useMapControl';

const SMOKE_RADIUS_WORLD = 150;

interface ReplayCanvasProps {
    replay: ParsedReplay;
    currentFrame: number;
    mapName: string;
    playerNames?: Record<string, string>;
    isBombPlanted?: boolean;
    bombPlantFrame?: number | null;
    activeDefuse?: { startFrame: number; hasKit: boolean } | null;
    bombExploded?: { frameIdx: number; x: number; y: number } | null;
    zoom: number;
    panOffset: { x: number; y: number };
    onZoomChange: (zoom: number) => void;
    onPanOffsetChange: (offset: { x: number; y: number }) => void;
    isPanMode: boolean;
    isAdmin?: boolean;
    onSmokeClick?: (entityId: number) => void;
    smokeClipEntityIds?: Set<number>;
    smokeBreaks?: SmokeBreak[];
    /** Map-control overlay: precomputed field for this round, or null when off/unavailable.
     *  On split-level maps this is the UPPER floor; `mapControlLower` carries the lower one. */
    mapControl?: MapControlData | null;
    mapControlLower?: MapControlData | null;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const SPLIT_GAP = 0; // no gap — dark background provides visual separation

function clampOffset(x: number, y: number, zoom: number, contentW: number, contentH?: number): { x: number; y: number } {
    const h = contentH ?? contentW;
    return {
        x: Math.max(-(contentW * zoom - contentW), Math.min(0, x)),
        y: Math.max(-(h * zoom - h), Math.min(0, y)),
    };
}

const ReplayCanvas: React.FC<ReplayCanvasProps> = ({
    replay, currentFrame, mapName, playerNames = {}, isBombPlanted = false, bombPlantFrame = null, activeDefuse = null,
    bombExploded = null,
    zoom, panOffset, onZoomChange, onPanOffsetChange, isPanMode,
    isAdmin = false, onSmokeClick,
    smokeClipEntityIds = new Set(),
    smokeBreaks = [],
    mapControl = null,
    mapControlLower = null,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [mapImage, setMapImage] = useState<HTMLImageElement | null>(null);
    const [lowerMapImage, setLowerMapImage] = useState<HTMLImageElement | null>(null);
    const mlConfig = multiLevelMaps[mapName] ?? null;
    const isMultiLevel = !!mlConfig;
    const [canvasSize, setCanvasSize] = useState(800);
    const [canvasWidth, setCanvasWidth] = useState(800);
    const [canvasHeight, setCanvasHeight] = useState(800);
    const [icons, setIcons] = useState<PreloadedIcons | null>(null);
    const [toast, setToast] = useState<string | null>(null);
    const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [hoverTarget, setHoverTarget] = useState<'smoke' | 'player' | null>(null);
    const hoverTargetRef = useRef<'smoke' | 'player' | null>(null);
    const projPathsRef = useRef(new Map<string, Array<Array<{ x: number; y: number }>>>());
    const projPathsUpperRef = useRef(new Map<string, Array<Array<{ x: number; y: number }>>>());
    const projPathsLowerRef = useRef(new Map<string, Array<Array<{ x: number; y: number }>>>());

    // Refs to avoid stale closures in native event listeners
    const zoomRef = useRef(zoom);
    const panOffsetRef = useRef(panOffset);
    const canvasSizeRef = useRef(canvasSize);
    const canvasWidthRef = useRef(canvasWidth);
    const canvasHeightRef = useRef(canvasHeight);
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);
    useEffect(() => { panOffsetRef.current = panOffset; }, [panOffset]);
    useEffect(() => { canvasSizeRef.current = canvasSize; }, [canvasSize]);
    useEffect(() => { canvasWidthRef.current = canvasWidth; }, [canvasWidth]);
    useEffect(() => { canvasHeightRef.current = canvasHeight; }, [canvasHeight]);

    // Layout dimensions for zoom/pan clamping (total content area)
    const layoutWRef = useRef(canvasSize);
    const layoutHRef = useRef(canvasSize);
    useEffect(() => {
        if (mlConfig) {
            const s = canvasSize / 1024;
            layoutWRef.current = mlConfig.upperCrop.w * s + SPLIT_GAP + mlConfig.lowerCrop.w * s;
            layoutHRef.current = Math.max(mlConfig.upperCrop.h * s, mlConfig.lowerCrop.h * s);
        } else {
            layoutWRef.current = canvasSize;
            layoutHRef.current = canvasSize;
        }
    }, [canvasSize, mlConfig]);

    // Pan drag state (refs to avoid re-renders during drag)
    const isDragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const panStart = useRef({ x: 0, y: 0 });
    const [isDraggingState, setIsDraggingState] = useState(false);

    useEffect(() => {
        if (mlConfig) {
            const upperSrc = mapImageMappings[mlConfig.upperImage];
            const lowerSrc = mapImageMappings[mlConfig.lowerImage];
            if (upperSrc) { const img = new Image(); img.onload = () => setMapImage(img); img.src = upperSrc; }
            if (lowerSrc) { const img = new Image(); img.onload = () => setLowerMapImage(img); img.src = lowerSrc; }
        } else {
            const src = mapImageMappings[mapName];
            if (!src) return;
            const img = new Image();
            img.onload = () => setMapImage(img);
            img.src = src;
            setLowerMapImage(null);
        }
    }, [mapName, mlConfig]);

    useEffect(() => {
        preloadIcons().then(setIcons).catch(() => {});
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (width <= 0 || height <= 0) continue;
                const dpr = Math.min(window.devicePixelRatio || 1, 2);
                const w = Math.min(Math.round(width * dpr), 2048);
                const h = Math.min(Math.round(height * dpr), 2048);

                if (mlConfig) {
                    // Multi-level: compute scale so combined panels fill available width
                    const uc = mlConfig.upperCrop;
                    const lc = mlConfig.lowerCrop;
                    const totalCropW = uc.w + lc.w; // in 1024-space pixels
                    const maxCropH = Math.max(uc.h, lc.h);
                    // Scale to fit width (accounting for gap in canvas pixels)
                    const scaleByW = (w - SPLIT_GAP) / totalCropW * 1024;
                    // Scale to fit height
                    const scaleByH = h / maxCropH * 1024;
                    // Use the smaller to fit both constraints
                    setCanvasSize(Math.min(scaleByW, scaleByH));
                } else {
                    // Single-level: square that fits the smaller dimension
                    setCanvasSize(Math.min(w, h));
                }
                setCanvasWidth(w);
                setCanvasHeight(h);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [mlConfig]);

    // Native wheel listener (must be non-passive to call preventDefault)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const handler = (e: WheelEvent) => {
            e.preventDefault();

            const rect = canvas.getBoundingClientRect();
            const cssToCanvasX = canvasWidthRef.current / rect.width;
            const cssToCanvasY = canvasHeightRef.current / rect.height;
            // Mouse position in canvas pixel coords, adjusted for map centering offset
            const mapOffX = (canvasWidthRef.current - canvasSizeRef.current) / 2;
            const mapOffY = (canvasHeightRef.current - canvasSizeRef.current) / 2;
            const mouseX = (e.clientX - rect.left) * cssToCanvasX - mapOffX;
            const mouseY = (e.clientY - rect.top) * cssToCanvasY - mapOffY;

            const oldZoom = zoomRef.current;
            const zoomDelta = e.deltaY > 0 ? -0.15 : 0.15;
            const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZoom + zoomDelta));
            if (newZoom === oldZoom) return;

            if (newZoom <= 1) {
                onZoomChange(1);
                onPanOffsetChange({ x: 0, y: 0 });
            } else {
                // Cursor-centered zoom: keep the point under the cursor fixed
                const worldX = (mouseX - panOffsetRef.current.x) / oldZoom;
                const worldY = (mouseY - panOffsetRef.current.y) / oldZoom;
                const newOffsetX = mouseX - worldX * newZoom;
                const newOffsetY = mouseY - worldY * newZoom;

                onZoomChange(newZoom);
                onPanOffsetChange(clampOffset(newOffsetX, newOffsetY, newZoom, layoutWRef.current, layoutHRef.current));
            }
        };

        canvas.addEventListener('wheel', handler, { passive: false });
        return () => canvas.removeEventListener('wheel', handler);
    }, [onZoomChange, onPanOffsetChange]);

    // Pan handlers
    const dragDistance = useRef(0);

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        dragDistance.current = 0;
        if (!isPanMode || zoom <= 1) return;
        isDragging.current = true;
        dragStart.current = { x: e.clientX, y: e.clientY };
        panStart.current = { x: panOffsetRef.current.x, y: panOffsetRef.current.y };
        setIsDraggingState(true);
        e.preventDefault();
    }, [isPanMode, zoom]);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isDragging.current) {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const dx = e.clientX - dragStart.current.x;
            const dy = e.clientY - dragStart.current.y;
            dragDistance.current = Math.sqrt(dx * dx + dy * dy);

            const rect = canvas.getBoundingClientRect();
            const cssToCanvasX = canvasWidthRef.current / rect.width;
            const cssToCanvasY = canvasHeightRef.current / rect.height;

            const canvasDx = (e.clientX - dragStart.current.x) * cssToCanvasX;
            const canvasDy = (e.clientY - dragStart.current.y) * cssToCanvasY;

            const newX = panStart.current.x + canvasDx;
            const newY = panStart.current.y + canvasDy;

            onPanOffsetChange(clampOffset(newX, newY, zoomRef.current, layoutWRef.current, layoutHRef.current));
            return;
        }

        // Hover hit detection for cursor feedback
        if (isPanMode && zoom > 1) {
            if (hoverTargetRef.current !== null) {
                hoverTargetRef.current = null;
                setHoverTarget(null);
            }
            return;
        }

        const canvas = canvasRef.current;
        const frame = replay.frames[currentFrame];
        const config = mapConfigs[mapName];
        if (!canvas || !frame || !config) {
            if (hoverTargetRef.current !== null) {
                hoverTargetRef.current = null;
                setHoverTarget(null);
            }
            return;
        }

        const rect = canvas.getBoundingClientRect();
        const cw = canvasWidthRef.current;
        const ch = canvasHeightRef.current;
        const cs = canvasSizeRef.current;
        const z = zoomRef.current;
        const po = panOffsetRef.current;

        const cssToCanvasX = cw / rect.width;
        const cssToCanvasY = ch / rect.height;

        const canvasMouseX = (e.clientX - rect.left) * cssToCanvasX;
        const canvasMouseY = (e.clientY - rect.top) * cssToCanvasY;

        let mapX: number, mapY: number;
        let hoverLevel: 'upper' | 'lower' | null = null;

        if (isMultiLevel && mlConfig) {
            const s = cs / 1024;
            const uc = mlConfig.upperCrop;
            const lc = mlConfig.lowerCrop;
            const upperW = uc.w * s;
            const totalW = upperW + SPLIT_GAP + lc.w * s;
            const totalH = Math.max(uc.h * s, lc.h * s);
            const offX = (cw - totalW) / 2;
            const offY = (ch - totalH) / 2;

            const localX = (canvasMouseX - offX - po.x) / z;
            const localY = (canvasMouseY - offY - po.y) / z;

            if (localX < upperW) {
                hoverLevel = 'upper';
                mapX = localX + uc.x * s;
                mapY = localY + uc.y * s;
            } else if (localX > upperW + SPLIT_GAP) {
                hoverLevel = 'lower';
                mapX = (localX - upperW - SPLIT_GAP) + lc.x * s;
                mapY = localY + lc.y * s;
            } else {
                if (hoverTargetRef.current !== null) {
                    hoverTargetRef.current = null;
                    setHoverTarget(null);
                }
                return;
            }
        } else {
            const mapOffX = (cw - cs) / 2;
            const mapOffY = (ch - cs) / 2;
            mapX = (canvasMouseX - mapOffX - po.x) / z;
            mapY = (canvasMouseY - mapOffY - po.y) / z;
        }

        let newTarget: 'smoke' | 'player' | null = null;

        // Check smokes (admin only)
        if (isAdmin && onSmokeClick) {
            const smokeRadiusCanvas = (SMOKE_RADIUS_WORLD / Math.abs(config.scale)) * (cs / 1024);
            for (let i = frame.world.smokes.length - 1; i >= 0; i--) {
                const smoke = frame.world.smokes[i];
                const pos = worldToCanvas(smoke.x, smoke.y, config, cs);
                const dx = mapX - pos.x;
                const dy = mapY - pos.y;
                if (Math.sqrt(dx * dx + dy * dy) <= smokeRadiusCanvas) {
                    newTarget = 'smoke';
                    break;
                }
            }
        }

        // Check players (for setpos copy)
        if (!newTarget) {
            const playerHitRadius = 12 * (cs / 1024);
            for (let i = frame.players.length - 1; i >= 0; i--) {
                const player = frame.players[i];
                if (!player?.isAlive) continue;
                if (hoverLevel && mlConfig) {
                    const pz = player.z;
                    const onLevel = pz >= mlConfig.zUpper ? hoverLevel === 'upper' : pz <= mlConfig.zLower ? hoverLevel === 'lower' : true;
                    if (!onLevel) continue;
                }
                const pos = worldToCanvas(player.x, player.y, config, cs);
                const dx = mapX - pos.x;
                const dy = mapY - pos.y;
                if (Math.sqrt(dx * dx + dy * dy) <= playerHitRadius) {
                    newTarget = 'player';
                    break;
                }
            }
        }

        if (hoverTargetRef.current !== newTarget) {
            hoverTargetRef.current = newTarget;
            setHoverTarget(newTarget);
        }
    }, [replay, currentFrame, mapName, isAdmin, onSmokeClick, isMultiLevel, mlConfig, isPanMode, zoom, onPanOffsetChange]);

    const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const wasDragged = dragDistance.current > 5;
        if (isDragging.current) {
            isDragging.current = false;
            setIsDraggingState(false);
        }

        if (!wasDragged) {
            const canvas = canvasRef.current;
            const frame = replay.frames[currentFrame];
            const config = mapConfigs[mapName];
            if (!canvas || !frame || !config) return;

            const rect = canvas.getBoundingClientRect();
            const cw = canvasWidthRef.current;
            const ch = canvasHeightRef.current;
            const cs = canvasSizeRef.current;
            const z = zoomRef.current;
            const po = panOffsetRef.current;

            const cssToCanvasX = cw / rect.width;
            const cssToCanvasY = ch / rect.height;

            // Mouse position in canvas pixel coords
            const canvasClickX = (e.clientX - rect.left) * cssToCanvasX;
            const canvasClickY = (e.clientY - rect.top) * cssToCanvasY;

            let mapX: number, mapY: number;
            let clickLevel: 'upper' | 'lower' | null = null;

            if (isMultiLevel && mlConfig) {
                // Determine which panel was clicked
                const s = cs / 1024;
                const uc = mlConfig.upperCrop;
                const lc = mlConfig.lowerCrop;
                const upperW = uc.w * s;
                const lowerW = lc.w * s;
                const totalW = upperW + SPLIT_GAP + lowerW;
                const totalH = Math.max(uc.h * s, lc.h * s);
                const offX = (cw - totalW) / 2;
                const offY = (ch - totalH) / 2;

                // Remove pan and zoom
                const localX = (canvasClickX - offX - po.x) / z;
                const localY = (canvasClickY - offY - po.y) / z;

                if (localX < upperW) {
                    clickLevel = 'upper';
                    // Convert back to full 1024 radar space
                    mapX = localX + uc.x * s;
                    mapY = localY + uc.y * s;
                } else if (localX > upperW + SPLIT_GAP) {
                    clickLevel = 'lower';
                    mapX = (localX - upperW - SPLIT_GAP) + lc.x * s;
                    mapY = localY + lc.y * s;
                } else {
                    return; // clicked on separator
                }
            } else {
                const mapOffX = (cw - cs) / 2;
                const mapOffY = (ch - cs) / 2;
                mapX = (canvasClickX - mapOffX - po.x) / z;
                mapY = (canvasClickY - mapOffY - po.y) / z;
            }

            // Smoke click detection (admin only)
            if (isAdmin && onSmokeClick) {
                const smokeRadiusCanvas = (SMOKE_RADIUS_WORLD / Math.abs(config.scale)) * (cs / 1024);
                for (let i = frame.world.smokes.length - 1; i >= 0; i--) {
                    const smoke = frame.world.smokes[i];
                    const pos = worldToCanvas(smoke.x, smoke.y, config, cs);
                    const dx = mapX - pos.x;
                    const dy = mapY - pos.y;
                    if (Math.sqrt(dx * dx + dy * dy) <= smokeRadiusCanvas) {
                        onSmokeClick(smoke.entityId);
                        return;
                    }
                }
            }

            // Player click — copy setpos command
            const playerHitRadius = 12 * (cs / 1024); // generous click target
            for (let i = frame.players.length - 1; i >= 0; i--) {
                const player = frame.players[i];
                if (!player?.isAlive) continue;
                // In multi-level mode, only match players on the clicked level
                if (clickLevel && mlConfig) {
                    const pz = player.z;
                    const onLevel = pz >= mlConfig.zUpper ? clickLevel === 'upper' : pz <= mlConfig.zLower ? clickLevel === 'lower' : true;
                    if (!onLevel) continue;
                }
                const pos = worldToCanvas(player.x, player.y, config, cs);
                const dx = mapX - pos.x;
                const dy = mapY - pos.y;
                if (Math.sqrt(dx * dx + dy * dy) <= playerHitRadius) {
                    const cmd = `setpos ${player.x} ${player.y} ${player.z}; setang ${player.pitch} ${player.yaw}`;
                    navigator.clipboard.writeText(cmd).then(() => {
                        const name = replay.roster[i] ? (playerNames[replay.roster[i].steamId] || `Player ${i + 1}`) : `Player ${i + 1}`;
                        if (toastTimer.current) clearTimeout(toastTimer.current);
                        setToast(`Copied setpos for ${name}`);
                        toastTimer.current = setTimeout(() => setToast(null), 3000);
                    }).catch(() => {});
                    return;
                }
            }
        }
    }, [isAdmin, onSmokeClick, replay, currentFrame, mapName, playerNames, isMultiLevel, mlConfig]);

    const handleMouseLeave = useCallback(() => {
        if (isDragging.current) {
            isDragging.current = false;
            setIsDraggingState(false);
        }
        if (hoverTargetRef.current !== null) {
            hoverTargetRef.current = null;
            setHoverTarget(null);
        }
    }, []);

    const killFeed = useMemo<KillFeedEntry[]>(() =>
        replay.events
            .filter((e): e is KillEvent => e.type === 'kill')
            .map(k => ({
                killerSlot: k.killerSlot,
                victimSlot: k.victimSlot,
                weaponId: k.weaponId,
                headshot: k.headshot,
                wallbang: k.wallbang,
                noscope: k.noscope,
                thrusmoke: k.thrusmoke,
                frameIdx: k.frameIdx,
            })),
        [replay.events]
    );

    const tracers = useMemo<TracerEntry[]>(() => {
        const t = replay.events
            .filter((e): e is BulletImpactEvent => e.type === 'bullet_impact')
            .map(e => ({
                shooterX: e.shooterX,
                shooterY: e.shooterY,
                impactX: e.impactX,
                impactY: e.impactY,
                frameIdx: e.frameIdx,
                shooterSlot: e.shooterSlot,
            }));
        // Debug: log event counts for this round
        const eventTypes: Record<string, number> = {};
        replay.events.forEach(e => { eventTypes[e.type] = (eventTypes[e.type] || 0) + 1; });
        console.log('[ReplayDebug] Events:', eventTypes, `| Frames: ${replay.frames.length} | Tracers: ${t.length}`);
        return t;
    }, [replay.events]);

    const grenadeTrajectories = useMemo<GrenadeTrajectory[]>(() => {
        const results: GrenadeTrajectory[] = [];
        const startEvents = replay.events.filter(
            (e): e is EntityStartEvent => e.type === 'smoke_start' || e.type === 'inferno_start' || e.type === 'decoy_start'
        );
        for (const evt of startEvents) {
            // Get thrower position from the frame at the event time
            const frame = replay.frames[Math.min(evt.frameIdx, replay.frames.length - 1)];
            if (!frame || evt.throwerSlot === 0xff || evt.throwerSlot >= frame.players.length) continue;
            const thrower = frame.players[evt.throwerSlot];
            if (!thrower) continue;
            results.push({
                throwerSlot: evt.throwerSlot,
                startX: thrower.x,
                startY: thrower.y,
                endX: evt.x,
                endY: evt.y,
                frameIdx: evt.frameIdx,
                type: evt.type,
                entityId: evt.entityId,
            });
        }
        return results;
    }, [replay.events, replay.frames]);

    // Build smoke entity -> team lookup
    const smokeTeams = useMemo<Record<number, 'T' | 'CT'>>(() => {
        const map: Record<number, 'T' | 'CT'> = {};
        const smokeStarts = replay.events.filter(
            (e): e is EntityStartEvent => e.type === 'smoke_start'
        );
        for (const evt of smokeStarts) {
            if (evt.throwerSlot !== 0xff && evt.throwerSlot < replay.roster.length) {
                map[evt.entityId] = replay.roster[evt.throwerSlot].team;
            }
        }
        return map;
    }, [replay.events, replay.roster]);

    // Flash detonation locations
    const flashPops = useMemo<FlashPopEntry[]>(() =>
        replay.events
            .filter((e): e is GrenadeDetonateEvent => e.type === 'flash_detonate' || e.type === 'he_detonate')
            .map(e => ({
                x: e.x, y: e.y, frameIdx: e.frameIdx,
                grenadeType: e.type === 'flash_detonate' ? 'flash' as const : 'he' as const,
            })),
        [replay.events]
    );

    // Flash blind events for computing remaining flash duration per player
    const flashBlinds = useMemo(() =>
        replay.events
            .filter((e): e is FlashBlindEvent => e.type === 'flash_blind')
            .map(e => ({ victimSlot: e.victimSlot, frameIdx: e.frameIdx, durationSeconds: e.durationSeconds })),
        [replay.events]
    );

    // Death markers — victim position at time of death
    const deathMarkers = useMemo<DeathMarker[]>(() =>
        replay.events
            .filter((e): e is KillEvent => e.type === 'kill')
            .map(k => {
                const frame = replay.frames[Math.min(k.frameIdx, replay.frames.length - 1)];
                if (!frame || k.victimSlot >= frame.players.length) return null;
                const victim = frame.players[k.victimSlot];
                const team = replay.roster[k.victimSlot]?.team;
                if (!team) return null;
                return { x: victim.x, y: victim.y, team, frameIdx: k.frameIdx };
            })
            .filter((m): m is DeathMarker => m !== null),
        [replay.events, replay.frames, replay.roster]
    );

    // Hit markers — attacker/victim positions from player_hurt events
    const hitMarkers = useMemo<HitMarkerEntry[]>(() =>
        replay.events
            .filter((e): e is PlayerHurtEvent => e.type === 'player_hurt' && e.attackerSlot != null && e.weaponId > 0 && e.weaponId < 70)
            .map(h => {
                const frame = replay.frames[Math.min(h.frameIdx, replay.frames.length - 1)];
                if (!frame || h.attackerSlot == null) return null;
                const attacker = h.attackerSlot < frame.players.length ? frame.players[h.attackerSlot] : null;
                const victim = h.victimSlot < frame.players.length ? frame.players[h.victimSlot] : null;
                if (!attacker || !victim) return null;
                return {
                    attackerX: attacker.x, attackerY: attacker.y,
                    victimX: victim.x, victimY: victim.y,
                    attackerSlot: h.attackerSlot,
                    frameIdx: h.frameIdx,
                };
            })
            .filter((m): m is HitMarkerEntry => m !== null),
        [replay.events, replay.frames]
    );

    // Fire entity -> team lookup
    const fireTeams = useMemo<Record<number, 'T' | 'CT'>>(() => {
        const map: Record<number, 'T' | 'CT'> = {};
        replay.events
            .filter((e): e is EntityStartEvent => e.type === 'inferno_start')
            .forEach(evt => {
                if (evt.throwerSlot !== 0xff && evt.throwerSlot < replay.roster.length)
                    map[evt.entityId] = replay.roster[evt.throwerSlot].team;
            });
        return map;
    }, [replay.events, replay.roster]);

    // Fire entity -> start frame lookup
    const fireStartFrames = useMemo<Record<number, number>>(() => {
        const map: Record<number, number> = {};
        replay.events
            .filter((e): e is EntityStartEvent => e.type === 'inferno_start')
            .forEach(evt => { map[evt.entityId] = evt.frameIdx; });
        return map;
    }, [replay.events]);

    // Multi-level: entity ID -> Z lookup for filtering smokes/fires to correct level
    const entityZData = useMemo<EntityZData>(() => {
        if (!isMultiLevel) return { entityZ: new Map(), slotThrows: new Map() };
        return buildEntityZLookup(replay.events, replay.frames);
    }, [replay.events, replay.frames, isMultiLevel]);

    // Render with zoom/pan transform
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !mapImage) return;
        // For multi-level maps, wait until both images are loaded
        if (isMultiLevel && !lowerMapImage) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const frame = replay.frames[currentFrame];
        if (!frame) return;

        // Check if bomb was defused
        const bombDefused = isBombPlanted && replay.events.some(
            e => e.type === 'bomb_defused' && e.frameIdx <= currentFrame
        );

        // Detect active defuse from player flags + scan backwards for start frame
        let computedDefuse: { startFrame: number; hasKit: boolean } | null = activeDefuse;
        if (!computedDefuse && isBombPlanted && !bombDefused) {
            for (let pi = 0; pi < frame.players.length; pi++) {
                const p = frame.players[pi];
                if (p.isAlive && (p.flags & (1 << 2))) { // bit 2 = isDefusing
                    const hasKit = !!(p.flags & (1 << 6)); // bit 6 = hasDefuse
                    let startFrame = currentFrame;
                    for (let fi = currentFrame - 1; fi >= Math.max(0, currentFrame - 700); fi--) {
                        const pf = replay.frames[fi]?.players[pi];
                        if (!pf || !(pf.flags & (1 << 2))) break;
                        startFrame = fi;
                    }
                    computedDefuse = { startFrame, hasKit };
                    break;
                }
            }
        }

        // Clear full canvas and fill with background
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        if (isMultiLevel && mlConfig && lowerMapImage) {
            // ── Split-view rendering (Nuke upper/lower) ──
            const uc = mlConfig.upperCrop;
            const lc = mlConfig.lowerCrop;
            const zU = mlConfig.zUpper;
                const zL = mlConfig.zLower;

            // Scale factor: map 1024 radar pixels to canvasSize
            const s = canvasSize / 1024;
            const upperPanelW = uc.w * s;
            const upperPanelH = uc.h * s;
            const lowerPanelW = lc.w * s;
            const lowerPanelH = lc.h * s;
            const totalW = upperPanelW + SPLIT_GAP + lowerPanelW;

            // Center the combined layout
            const offsetX = (canvasWidth - totalW) / 2;
            const offsetY = (canvasHeight - Math.max(upperPanelH, lowerPanelH)) / 2;

            // panelX is the X offset of each panel in "layout space" (before zoom/pan)
            const upperPanelX = 0;
            const lowerPanelX = upperPanelW + SPLIT_GAP;
            const maxPanelH = Math.max(upperPanelH, lowerPanelH);

            const renderLevel = (
                level: 'upper' | 'lower',
                img: HTMLImageElement,
                crop: { x: number; y: number; w: number; h: number },
                panelX: number,
                panelW: number,
                panelH: number,
            ) => {
                const filteredFrame = filterFrameByLevel(frame, level, zU, zL, mlConfig.bothZone);
                const filteredWorld = filterWorldByLevel(filteredFrame.world, entityZData, frame, currentFrame, level, zU, zL, mlConfig.bothZone);
                const levelFrame = { ...filteredFrame, world: filteredWorld };

                // Filter event-based overlays by Z level
                const levelTracers = filterByPlayerZ(tracers, replay.frames, t => t.shooterSlot, level, zU, zL);
                const levelHitMarkers = filterByPlayerZ(hitMarkers, replay.frames, h => h.attackerSlot, level, zU, zL);
                // Trajectories: use the entity's landing Z (from entity Z lookup), not thrower's current Z
                const midZ = (zU + zL) / 2;
                const levelGrenadeTrajectories = grenadeTrajectories.filter(g => {
                    const z = entityZData.entityZ.get(g.entityId);
                    if (z === undefined) return false;
                    return level === 'upper' ? z >= midZ : z < midZ;
                });
                const levelFlashPops = flashPops.filter(fp => {
                    const origEvt = replay.events.find(
                        (e): e is GrenadeDetonateEvent =>
                            (e.type === 'flash_detonate' || e.type === 'he_detonate') && e.frameIdx === fp.frameIdx
                    );
                    if (!origEvt || origEvt.throwerSlot === 0xff) return false;
                    const f = replay.frames[Math.min(fp.frameIdx, replay.frames.length - 1)];
                    if (!f || origEvt.throwerSlot >= f.players.length) return false;
                    const tz = f.players[origEvt.throwerSlot].z;
                    return tz >= zU ? level === 'upper' : tz <= zL ? level === 'lower' : true;
                });
                const levelDeathMarkers = deathMarkers.filter(dm => {
                    const f = replay.frames[Math.min(dm.frameIdx, replay.frames.length - 1)];
                    if (!f) return false;
                    for (const p of f.players) {
                        if (Math.abs(p.x - dm.x) < 2 && Math.abs(p.y - dm.y) < 2) {
                            return p.z >= zU ? level === 'upper' : p.z <= zL ? level === 'lower' : true;
                        }
                    }
                    return false;
                });

                // Clip to this panel's bounds (in layout space, inside the global zoom+pan)
                ctx.save();
                ctx.beginPath();
                ctx.rect(panelX, 0, panelW, panelH);
                ctx.clip();

                // Translate so worldToCanvas coords align with this panel's crop region
                ctx.translate(panelX - crop.x * s, -crop.y * s);

                const levelControl = level === 'upper' ? mapControl : mapControlLower;
                const levelStates = levelControl?.statesAtFrame(currentFrame) ?? null;
                const levelUnderlay = levelControl && levelStates
                    ? (c: CanvasRenderingContext2D, size: number) =>
                        drawMapControl(c, levelControl.grid, levelControl.cells, levelStates, size, currentFrame)
                    : null;

                const levelProjPaths = level === 'upper' ? projPathsUpperRef.current : projPathsLowerRef.current;
                renderFrame(ctx, levelFrame, replay.roster, mapName, img, canvasSize,
                    killFeed, currentFrame, playerNames, levelTracers, levelGrenadeTrajectories,
                    smokeTeams, icons, levelFlashPops, fireTeams, fireStartFrames,
                    levelProjPaths, flashBlinds, levelDeathMarkers, levelHitMarkers,
                    isBombPlanted, bombPlantFrame, computedDefuse, bombDefused, smokeClipEntityIds,
                    smokeBreaks, bombExploded, levelUnderlay);

                // Level label (in layout space)
                ctx.translate(-(panelX - crop.x * s), crop.y * s); // undo crop translate
                ctx.translate(panelX, 0);
                ctx.font = `bold ${Math.max(10, Math.round(canvasSize / 80))}px Arial`;
                ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillText(level === 'upper' ? 'UPPER' : 'LOWER', panelW / 2, 6);

                ctx.restore();
            };

            // Apply global zoom+pan for the entire combined layout
            ctx.save();
            ctx.translate(offsetX + panOffset.x, offsetY + panOffset.y);
            ctx.scale(zoom, zoom);

            renderLevel('upper', mapImage, uc, upperPanelX, upperPanelW, upperPanelH);
            renderLevel('lower', lowerMapImage, lc, lowerPanelX, lowerPanelW, lowerPanelH);

            ctx.restore();

        } else {
            // ── Single-level rendering (all other maps) ──
            const mapOffsetX = (canvasWidth - canvasSize) / 2;
            const mapOffsetY = (canvasHeight - canvasSize) / 2;
            ctx.save();
            ctx.translate(panOffset.x + mapOffsetX, panOffset.y + mapOffsetY);
            ctx.scale(zoom, zoom);

            // Map control is a single-plane field, so it only applies to the non-split renderer.
            const controlStates = mapControl?.statesAtFrame(currentFrame) ?? null;
            const controlUnderlay = mapControl && controlStates
                ? (c: CanvasRenderingContext2D, size: number) =>
                    drawMapControl(c, mapControl.grid, mapControl.cells, controlStates, size, currentFrame)
                : null;

            renderFrame(ctx, frame, replay.roster, mapName, mapImage, canvasSize, killFeed, currentFrame, playerNames, tracers, grenadeTrajectories, smokeTeams, icons, flashPops, fireTeams, fireStartFrames, projPathsRef.current, flashBlinds, deathMarkers, hitMarkers, isBombPlanted, bombPlantFrame, computedDefuse, bombDefused, smokeClipEntityIds, smokeBreaks, bombExploded, controlUnderlay);

            ctx.restore();
        }
    }, [currentFrame, mapImage, lowerMapImage, canvasSize, canvasWidth, canvasHeight, replay, mapName, icons, isBombPlanted, bombPlantFrame, zoom, panOffset, isMultiLevel, mlConfig, entityZData, activeDefuse, smokeClipEntityIds, bombExploded, mapControl, mapControlLower]);

    // Cursor style — pointer when hovering over clickable elements (players, smokes)
    const cursor = isPanMode && zoom > 1
        ? (isDraggingState ? 'grabbing' : 'grab')
        : (hoverTarget ? 'pointer' : 'default');

    return (
        <div ref={containerRef} style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
            <canvas
                ref={canvasRef}
                width={canvasWidth}
                height={canvasHeight}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                style={{
                    display: 'block',
                    width: canvasWidth / Math.min(window.devicePixelRatio || 1, 2),
                    height: canvasHeight / Math.min(window.devicePixelRatio || 1, 2),
                    maxWidth: '100%',
                    maxHeight: '100%',
                    cursor,
                }}
            />
            {toast && (
                <div style={{
                    position: 'absolute',
                    bottom: 12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    color: '#e2e8f0',
                    padding: '8px 16px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    zIndex: 30,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    pointerEvents: 'none',
                }}>
                    {toast} — <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 400 }}>paste in CS2 console to teleport</span>
                </div>
            )}
        </div>
    );
};

export default ReplayCanvas;
